import { useCallback, useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { useHistory, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { withStyles } from '@aura/components/styles'
import Box from '@aura/components/box'
import Typography from '@aura/components/typography'
import Grid from '@aura/components/grid'
import { Button } from '@aura/components'
import { useTranslation } from 'react-i18next'
import { useAuraThemeSwitcher } from '@aura/components/themes'
import { cookieNameSessionTimeout, cookieNameLoginAppCookie, cookieSessionTimeout } from '../../constants/app'
import { LOGIN_ROUTE, LOGOUT_ROUTE } from '../../constants/routes'
import { showLoader } from '../../utils/loader-actions'
import AppBar from '../app-bar-tool'
import Footer from '../footer-new'
import SUPPORTED_THEMES from '../../constants/theme'
import styles from './styles'


const TIMEOUT_ILLUSTRATIONS = {
    [SUPPORTED_THEMES.DARK]: '/img/logos/time-out-dark.png',
    [SUPPORTED_THEMES.LIGHT]: '/img/logos/time-out-light.svg'
}


const Component = ({
    classes,
    eventCleanup
}) => {
    const { t: translate } = useTranslation()
    const { theme } = useAuraThemeSwitcher()
    const paletteMode = theme?.palette?.mode
    const history = useHistory()
    const location = useLocation()
    const params = new URLSearchParams(location.search)
    const timeoutFlag = params.get('timeout')
    const [ cookies, setCookie, removeCookie ] = useCookies([ cookieNameLoginAppCookie, cookieNameSessionTimeout ])
    const redirectTo = cookies[cookieNameLoginAppCookie]
    const title = translate('sessionTimeout')
    const verbiage = translate('sessionTimeoutInactivity')
    const timeoutIllustration = TIMEOUT_ILLUSTRATIONS[paletteMode] || TIMEOUT_ILLUSTRATIONS[SUPPORTED_THEMES.LIGHT]

    const handleOnclick = useCallback(() => {
        if (redirectTo) {
            window.location.pathname = `/${redirectTo}/login`
        } else {
            showLoader()
            history.push(LOGIN_ROUTE)
        }
    }, [ redirectTo, history ])


    useEffect(() => {
        const createSessionTimeoutEvent = async dataObj => {
            try {
                await eventCleanup(dataObj)
                // Only remove cookie after successful API call
                removeCookie(cookieSessionTimeout, { path: '/' })
                console.log('Session timeout cleanup completed and cookie removed')
            } catch (error) {
                console.error('Failed to cleanup session timeout:', error)
            }
        }

        const runTimeoutFlow = async () => {
            const isTimeoutRedirect = timeoutFlag === 'true'
            const isAlreadyProcessed = Boolean(cookies[cookieNameSessionTimeout])

            if (!isTimeoutRedirect || isAlreadyProcessed) {
                return
            }

            setCookie(cookieNameSessionTimeout, true, { path: '/' })

            const dataObj = cookies[cookieSessionTimeout] || null
            if (dataObj) {
                await createSessionTimeoutEvent(dataObj)
            }

            history.push(LOGOUT_ROUTE)
        }

        runTimeoutFlow()
    }, [])

    return (
        <>
            <AppBar />
            <Grid container className={classes.gridContainerCenter}>
                <Grid className={classes.noAccessRoot} md={5} sm={8} xs={12}>
                    <Box className={classes.header}>
                        <Box className={classes.illustrationWrapper}>
                            <Box className={classes.illustrationBackground}>
                                <Box className={classes.illustrationIcon}>
                                    <img className={classes.illustrationImage} src={timeoutIllustration} alt={translate('sessionTimeout')} />
                                </Box>
                            </Box>
                        </Box>
                        <Box className={classes.copyWrapper}>
                            <Typography className={classes.title}>{title}</Typography>
                            <Typography className={classes.verbiage}>{verbiage}</Typography>
                            <Typography className={classes.verbiageHelpText}>{translate('errorPage.helpText')}</Typography>
                            <Button
                                className={classes.returnButton}
                                onClick={handleOnclick}
                                variant='contained'
                            >
                                {translate('errorPage.returnToLogin')}
                            </Button>
                            
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <Grid container className={classes.gridContainerFooter}>
                <Footer />
            </Grid>
        </>
    )
}

Component.displayName = 'OktaRegistrationWizard.ErrorPage'

Component.propTypes = {
    classes: PropTypes.shape({
        heading: PropTypes.string.isRequired,
        link: PropTypes.string.isRequired,
        registrationRoot: PropTypes.string.isRequired,
        root: PropTypes.string.isRequired,
        verbiage: PropTypes.string.isRequired
    }),
    eventCleanup: PropTypes.func.isRequired
}
export default withStyles(Component, styles)
